import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash, Envelope, ShieldCheck, Clock, Database } from "@phosphor-icons/react";

/**
 * Public Account & Data Deletion page
 * ───────────────────────────────────
 * Required by Google Play Data Safety. Must be reachable WITHOUT installing
 * the app. Two paths:
 *   • Signed-in users → instant self-service delete via POST /users/me/delete-account
 *   • Signed-out users → anonymous form posting to /account-deletion-request
 *     (super-admin processes within 7 days)
 *
 * The page also lists exactly what gets deleted, what's retained for legal
 * reasons, and the SLA — these are the disclosures Google audits.
 */
export default function AccountDeletionPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestEmail, setRequestEmail] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submitInstant = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error("Type 'DELETE' to confirm.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/users/me/delete-account", { confirm: "DELETE" });
      toast.success(`Account deleted — ${data.rows_deleted} records wiped.`);
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed. Please email support.");
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account-deletion-request", { email: requestEmail, reason: requestReason });
      setSubmitted(true);
      toast.success("Request received. We'll email you within 7 days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <main className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="account-deletion-page">
        <div className="flex items-center gap-3 mb-2">
          <Trash size={28} weight="duotone" className="text-rose-600" />
          <h1 className="font-serif-display text-3xl sm:text-4xl font-light">Delete your account &amp; data</h1>
        </div>
        <p className="text-sm text-muted mb-8 leading-relaxed">
          AstroKuberChat respects your right to be forgotten. You can permanently
          delete your account and every piece of data tied to it at any time
          using the options below.
        </p>

        {/* ── What gets deleted ────────────────────────── */}
        <section className="card-surface rounded-lg p-5 mb-6 border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <Database size={18} weight="duotone" className="text-stone-700" />
            <h2 className="font-semibold text-base">What gets deleted</h2>
          </div>
          <ul className="text-xs sm:text-sm text-stone-700 space-y-1.5 list-disc list-inside leading-relaxed">
            <li>Profile (name, email, phone, birth details)</li>
            <li>Every chat thread and message you sent or received</li>
            <li>Saved kundalis, horoscopes, premium reports</li>
            <li>Wallet balance &amp; transaction history (recharges, deductions, payouts)</li>
            <li>Voice/PSTN call logs &amp; recordings</li>
            <li>Reviews and ratings you posted</li>
            <li>Push-notification subscriptions</li>
            <li>OAuth tokens &amp; active sessions</li>
          </ul>
        </section>

        {/* ── What we keep (legal) ──────────────────────── */}
        <section className="card-surface rounded-lg p-5 mb-6 border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} weight="duotone" className="text-stone-700" />
            <h2 className="font-semibold text-base">What we retain (legal compliance)</h2>
          </div>
          <ul className="text-xs sm:text-sm text-stone-700 space-y-1.5 list-disc list-inside leading-relaxed">
            <li>
              Anonymized payment receipts (invoice number + amount only — no
              personal data) for <b>7 years</b> as required by tax law (GST in
              India / IRS in US).
            </li>
            <li>
              A single audit row in our deletion log (your email + timestamp +
              row counts) so we can prove the deletion happened during a future
              audit. No other personal data is retained.
            </li>
          </ul>
        </section>

        {/* ── SLA ────────────────────────── */}
        <section className="card-surface rounded-lg p-5 mb-8 border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} weight="duotone" className="text-stone-700" />
            <h2 className="font-semibold text-base">Timeline</h2>
          </div>
          <ul className="text-xs sm:text-sm text-stone-700 space-y-1.5 list-disc list-inside leading-relaxed">
            <li>
              <b>If you&rsquo;re signed in below</b> — your data is deleted
              instantly the moment you confirm.
            </li>
            <li>
              <b>If you submit the email request form</b> — your data is deleted
              within <b>7 calendar days</b>. We&rsquo;ll send you a
              confirmation email when complete.
            </li>
            <li>
              <b>Backups</b> — fully scrubbed from rolling backups within 30
              days.
            </li>
          </ul>
        </section>

        {/* ── Instant self-service for signed-in users ─── */}
        {!loading && user && (
          <section className="card-surface rounded-lg p-5 mb-6 border-2 border-rose-300 bg-rose-50/50" data-testid="instant-delete-panel">
            <h2 className="font-semibold text-base text-rose-900 mb-1">
              You&rsquo;re signed in as <span className="font-mono">{user.email}</span>
            </h2>
            <p className="text-xs sm:text-sm text-rose-800 mb-4 leading-relaxed">
              Delete your account and all associated data right now. This
              action cannot be undone.
            </p>
            <label className="block text-xs font-semibold text-rose-900 mb-1.5" htmlFor="confirm-input">
              Type <code className="px-1 bg-rose-100 rounded">DELETE</code> to confirm:
            </label>
            <input
              id="confirm-input"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              className="w-full px-3 py-2 rounded-md border border-rose-300 bg-white text-sm font-mono focus:ring-2 focus:ring-rose-500 outline-none mb-3"
              data-testid="instant-delete-confirm-input"
            />
            <button
              type="button"
              onClick={submitInstant}
              disabled={busy || confirmText.trim().toUpperCase() !== "DELETE"}
              className="w-full sm:w-auto px-6 py-2.5 rounded-md bg-rose-600 text-white text-sm font-bold hover:bg-rose-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="instant-delete-btn"
            >
              {busy ? "Deleting\u2026" : "Permanently delete my account"}
            </button>
          </section>
        )}

        {/* ── Email request form for everyone else ────── */}
        <section className="card-surface rounded-lg p-5 border border-stone-200" data-testid="request-form-panel">
          <div className="flex items-center gap-2 mb-3">
            <Envelope size={18} weight="duotone" className="text-stone-700" />
            <h2 className="font-semibold text-base">
              {user ? "Or, submit a deletion request via email instead" : "Submit a deletion request"}
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-stone-700 mb-4 leading-relaxed">
            Lost access to your account? Tell us the email you signed up with
            and we&rsquo;ll wipe everything within 7 days. We may email you
            back to verify ownership.
          </p>
          {submitted ? (
            <div className="px-3 py-3 rounded-md bg-emerald-50 border border-emerald-300 text-sm text-emerald-900" data-testid="request-submitted">
              Your request has been received. We&rsquo;ll email you within 7 days
              to confirm deletion.
            </div>
          ) : (
            <form onSubmit={submitRequest} className="space-y-3">
              <div>
                <label htmlFor="req-email" className="block text-xs font-semibold text-stone-700 mb-1">Account email</label>
                <input
                  id="req-email"
                  type="email"
                  required
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 rounded-md border border-stone-300 bg-white text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="request-email-input"
                />
              </div>
              <div>
                <label htmlFor="req-reason" className="block text-xs font-semibold text-stone-700 mb-1">Reason (optional)</label>
                <textarea
                  id="req-reason"
                  rows={3}
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  placeholder="Not needed for us to delete your account &mdash; just helps us improve."
                  className="w-full px-3 py-2 rounded-md border border-stone-300 bg-white text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="request-reason-input"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full sm:w-auto px-5 py-2.5 rounded-md bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 disabled:opacity-50"
                data-testid="request-submit-btn"
              >
                {busy ? "Submitting\u2026" : "Submit deletion request"}
              </button>
            </form>
          )}
        </section>

        <p className="text-[11px] text-muted mt-8 leading-relaxed">
          Need help? Email{" "}
          <a className="underline" href="mailto:support@astrokuberchat.com">
            support@astrokuberchat.com
          </a>{" "}
          and we&rsquo;ll act within 24 hours.
        </p>
      </main>
    </PageShell>
  );
}
